import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft, Package, Inbox, Lock, LogOut, ImageIcon } from "lucide-react";
import { GalleryManager } from "../components/GalleryManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getEnquiries, deleteEnquiry, adminLogin,
} from "../api";
import { toast } from "../hooks/use-toast";

const emptyForm = { name: "", desc: "", image: "", category: "General", brochure: "" };

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem("admin_token", token);
      toast({ title: "Welcome back" });
      onSuccess();
    } catch (err) {
      toast({ title: "Invalid password", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/bhawani-logo.png" alt="Bhawani Kitchen Equipment" className="h-14 w-auto object-contain mb-3" />
          <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <Lock size={15} /> Admin Dashboard Login
          </div>
        </div>
        <Label>Password</Label>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter admin password"
          className="mt-1.5 mb-4"
          autoFocus
        />
        <Button type="submit" disabled={loading} className="brand-btn w-full">
          {loading ? "Signing in..." : "Sign In"}
        </Button>
        <Link to="/" className="block text-center text-sm text-slate-400 hover:text-slate-600 mt-4">
          Back to site
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [galleryCount, setGalleryCount] = useState(0);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  const handleAuthError = (e) => {
    if (e?.response?.status === 401) logout();
  };

  const loadProducts = () => getProducts().then(setProducts).catch(() => {});
  const loadEnquiries = () => getEnquiries().then(setEnquiries).catch(handleAuthError);

  useEffect(() => {
    if (!authed) return;
    loadProducts();
    loadEnquiries();
  }, [authed]);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, desc: p.desc, image: p.image, category: p.category || "General", brochure: p.brochure || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.image) {
      toast({ title: "Name and image URL are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateProduct(editing.id, form);
        toast({ title: "Product updated" });
      } else {
        await createProduct(form);
        toast({ title: "Product added" });
      }
      setOpen(false);
      await loadProducts();
    } catch (e) {
      handleAuthError(e);
      toast({ title: "Something went wrong", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (id) => {
    try {
      await deleteProduct(id);
      toast({ title: "Product deleted" });
      loadProducts();
    } catch (e) {
      handleAuthError(e);
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await deleteEnquiry(id);
      toast({ title: "Enquiry deleted" });
      loadEnquiries();
    } catch (e) {
      handleAuthError(e);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-[color:var(--brand-navy)] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/bhawani-logo.png" alt="Bhawani Kitchen Equipment" className="h-11 w-auto object-contain bg-white rounded-md px-1" />
            <div>
              <div className="font-head font-bold leading-tight">Bhawani Kitchen Equipment</div>
              <div className="text-[11px] tracking-widest text-cyan-200">ADMIN DASHBOARD</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-cyan-100 hover:text-white transition-colors">
              <LogOut size={16} /> Logout
            </button>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-cyan-100 hover:text-white transition-colors">
              <ArrowLeft size={16} /> Back to site
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="products">
          <TabsList className="mb-6">
            <TabsTrigger value="products" className="gap-2"><Package size={16} /> Products ({products.length})</TabsTrigger>
            <TabsTrigger value="enquiries" className="gap-2"><Inbox size={16} /> Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="gallery" className="gap-2" data-testid="admin-gallery-tab"><ImageIcon size={16} /> Gallery ({galleryCount})</TabsTrigger>
          </TabsList>

          <TabsContent value="gallery" forceMount className="data-[state=inactive]:hidden">
            <GalleryManager onAuthError={handleAuthError} onCount={setGalleryCount} />
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-head font-bold text-xl text-[color:var(--brand-navy)]">Manage Products</h2>
              <Button onClick={openCreate} className="gap-2 brand-btn"><Plus size={16} /> Add Product</Button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Category</TableHead>
                    <TableHead className="hidden lg:table-cell">Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <img src={p.image} alt={p.name} className="h-12 w-12 rounded-md object-cover" />
                      </TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{p.category}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell max-w-xs truncate text-slate-500">{p.desc}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="icon" variant="outline" onClick={() => openEdit(p)}><Pencil size={15} /></Button>
                          <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => removeProduct(p.id)}><Trash2 size={15} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {products.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No products yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries">
            <h2 className="font-head font-bold text-xl text-[color:var(--brand-navy)] mb-4">Customer Enquiries</h2>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden md:table-cell">Message</TableHead>
                    <TableHead className="hidden sm:table-cell">Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-[color:var(--brand-blue)]">{e.email}</TableCell>
                      <TableCell className="hidden md:table-cell max-w-xs truncate text-slate-500">{e.message || "—"}</TableCell>
                      <TableCell className="hidden sm:table-cell text-slate-400 text-sm">
                        {new Date(e.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => removeEnquiry(e.id)}><Trash2 size={15} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {enquiries.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">No enquiries yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Visi Cooler" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Refrigeration" />
            </div>
            <div className="space-y-1.5">
              <Label>Image URL</Label>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <Label>Brochure PDF URL (optional)</Label>
              <Input value={form.brochure} onChange={(e) => setForm({ ...form, brochure: e.target.value })} placeholder="https://.../brochure.pdf" data-testid="product-brochure-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={3} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Short product description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="brand-btn" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
